// src/pages/dashboards/company/CompanyClaims.jsx
// The claims board. KPI strip across statuses, a glass list filtered by status,
// and a read-only detail modal (items | proof | messages) that settles claims:
// pay / query / reject. Query needs a note; reject's note is optional.
// Pay/query/reject are only valid while submitted or queried.

import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as companyApi from "../../../api/company";
import { tokenStore } from "../../../api/client";
import { Button, Badge, Modal, Loading, EmptyState } from "../../../components/ui";
import { money, date, dateTime } from "../../../lib/format";
import { useToast } from "../../../context/ToastContext";

const TABS = [
  { key: "submitted", label: "To review", color: "#3B82F6" },
  { key: "queried", label: "Queried", color: "#F59E0B" },
  { key: "paid", label: "Paid", color: "#12B266" },
  { key: "rejected", label: "Rejected", color: "#EF4444" },
];

const STATUS_TONE = { submitted: "blue", queried: "amber", paid: "green", rejected: "red" };
const STATUS_COLOR = { submitted: "#3B82F6", queried: "#F59E0B", paid: "#12B266", rejected: "#EF4444" };

// a claim is still actionable (pay/query/reject) while submitted or queried
const isOpen = (status) => status === "submitted" || status === "queried";

// glass surface
const Glass = ({ className = "", children }) => (
  <div className={`glass rounded-2xl border border-white/20 dark:border-white/5 shadow-xl shadow-black/[0.04] dark:shadow-black/30 ${className}`}>
    {children}
  </div>
);

// initials avatar from a name
const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const VALID_TABS = ["submitted", "queried", "paid", "rejected"];

export default function CompanyClaims() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const initial = VALID_TABS.includes(params.get("status")) ? params.get("status") : "submitted";
  const [tab, setTab] = useState(initial);
  const [claims, setClaims] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [counts, setCounts] = useState({}); // per-status { count, total }

  // keep tab in sync with the URL (?status=) so deep links + back/forward work
  const selectTab = (key) => {
    setTab(key);
    setParams(key === "submitted" ? {} : { status: key }, { replace: true });
  };
  useEffect(() => {
    const s = params.get("status");
    if (s && VALID_TABS.includes(s) && s !== tab) setTab(s);
    // eslint-disable-next-line
  }, [params]);

  const load = useCallback(async () => {
    setClaims(null);
    try {
      setClaims(await companyApi.listClaims(tab));
    } catch (e) {
      toast.error(e.message);
      setClaims([]);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  // KPI strip: all four statuses in parallel
  const loadCounts = useCallback(() => {
    Promise.all(
      TABS.map((tb) =>
        companyApi.listClaims(tb.key)
          .then((rows) => ({ key: tb.key, count: rows.length, total: rows.reduce((s, c) => s + Number(c.total || 0), 0) }))
          .catch(() => ({ key: tb.key, count: 0, total: 0 }))
      )
    ).then((arr) => {
      const map = {};
      arr.forEach((a) => { map[a.key] = a; });
      setCounts(map);
    });
  }, []);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-bold">Claims board</h1>

      {/* KPI strip - tap a tile to filter */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {TABS.map((tb) => {
          const c = counts[tb.key] || { count: 0, total: 0 };
          const active = tab === tb.key;
          return (
            <button
              key={tb.key}
              onClick={() => selectTab(tb.key)}
              className={`glass rounded-2xl border p-4 flex flex-col items-center text-center transition-all hover:-translate-y-0.5 ${
                active ? "border-tb-brand/50 ring-1 ring-tb-brand/30" : "border-white/20 dark:border-white/5"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: tb.color }} />
                <span className="text-xs font-semibold text-tb-muted uppercase tracking-wider">{tb.label}</span>
              </div>
              <p className="text-2xl font-bold leading-none">{c.count}</p>
              <p className="text-sm text-tb-muted mt-1.5 tabular-nums">{money(c.total)}</p>
            </button>
          );
        })}
      </div>

      {/* list */}
      {!claims ? (
        <Loading label="Loading claims" />
      ) : claims.length === 0 ? (
        <Glass className="p-2">
          <EmptyState title="Nothing here" hint="No claims with this status." />
        </Glass>
      ) : (
        <Glass className="divide-y divide-tb-border/60 dark:divide-tb-d-border/60 overflow-hidden">
          {claims.map((c) => (
            <button
              key={c.id}
              onClick={() => setOpenId(c.id)}
              className="w-full text-left px-4 sm:px-5 py-4 flex items-center gap-4 hover:bg-white/40 dark:hover:bg-white/[0.03] transition-colors border-l-[3px]"
              style={{ borderLeftColor: STATUS_COLOR[c.status] || "transparent" }}
            >
              {/* avatar */}
              <div className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-sm font-semibold text-white"
                   style={{ backgroundColor: STATUS_COLOR[c.status] || "#888" }}>
                {initials(c.submitter_name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{c.submitter_name}</span>
                  <Badge tone={STATUS_TONE[c.status]}>{c.status}</Badge>
                </div>
                <p className="text-sm text-tb-muted truncate">
                  {c.submitter_email}
                  {c.reference ? ` · ${c.reference}` : ""}
                  {` · ${c.item_count} item${c.item_count === 1 ? "" : "s"}`}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-bold tabular-nums">{money(c.total)}</p>
                <p className="text-xs text-tb-muted">{date(c.submitted_at || c.created_at)}</p>
              </div>
            </button>
          ))}
        </Glass>
      )}

      <ClaimModal
        id={openId}
        onClose={() => setOpenId(null)}
        onActioned={() => { setOpenId(null); load(); loadCounts(); }}
      />
    </div>
  );
}

// ---- detail modal -------------------------------------------------------
function ClaimModal({ id, onClose, onActioned }) {
  const toast = useToast();
  const [claim, setClaim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // null | 'query' | 'reject'
  const [note, setNote] = useState("");
  const [selItem, setSelItem] = useState(null); // item id whose proof is shown
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState("");
  const [sending, setSending] = useState(false);

  // load claim + messages when id changes
  useEffect(() => {
    if (!id) return;
    setClaim(null); setMode(null); setNote(""); setSelItem(null);
    setMessages([]); setMsgInput("");
    companyApi.getClaim(id)
      .then((c) => {
        setClaim(c);
        const first = (c.items || []).find((it) => it.has_image);
        if (first) setSelItem(first.id);
      })
      .catch((e) => toast.error(e.message));
    companyApi.getClaimMessages(id).then(setMessages).catch(() => setMessages([]));
  }, [id]);

  const sendMsg = async () => {
    const body = msgInput.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const msg = await companyApi.sendClaimMessage(id, body);
      setMessages((m) => [...m, msg]);
      setMsgInput("");
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      onActioned();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onPay = () => act(() => companyApi.payClaim(claim.id), "Claim marked paid");
  const onQuery = () => {
    if (!note.trim()) return toast.error("Add a note so the sender knows what to fix");
    act(() => companyApi.queryClaim(claim.id, note.trim()), "Sent back with a query");
  };
  const onReject = () => act(() => companyApi.rejectClaim(claim.id, note.trim()), "Claim rejected");

  return (
    <Modal open={!!id} onClose={onClose} title="Claim" width="max-w-6xl" bodyClassName="p-0">
      {!claim ? (
        <Loading label="Loading claim" />
      ) : (
        <div>
          {/* header */}
          <div className="px-6 py-4 border-b border-tb-border dark:border-tb-d-border">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-full shrink-0 flex items-center justify-center text-sm font-semibold text-white"
                     style={{ backgroundColor: STATUS_COLOR[claim.status] || "#888" }}>
                  {initials(claim.submitter_name)}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{claim.submitter_name}</p>
                  <p className="text-sm text-tb-muted truncate">{claim.submitter_email}</p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-lg font-bold tabular-nums">{money(claim.total)}</p>
                <Badge tone={STATUS_TONE[claim.status]}>{claim.status}</Badge>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-tb-muted">
              {claim.reference && <span>Ref: {claim.reference}</span>}
              <span>Submitted: {dateTime(claim.submitted_at)}</span>
              {claim.paid_at && <span>Paid: {dateTime(claim.paid_at)}</span>}
              {claim.rejected_at && <span>Rejected: {dateTime(claim.rejected_at)}</span>}
            </div>
            {claim.notes && <p className="mt-3 text-sm">{claim.notes}</p>}
            {claim.company_note && (
              <p className="mt-2 text-sm text-tb-amber">Your last note: {claim.company_note}</p>
            )}
          </div>

          {/* items | proof | messages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:divide-x divide-tb-border dark:divide-tb-d-border">
            {/* items */}
            <div className="p-5">
              <p className="text-sm font-semibold mb-3">Items</p>
              <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
                {(claim.items || []).map((it) => {
                  const on = selItem === it.id;
                  return (
                    <button
                      key={it.id}
                      disabled={!it.has_image}
                      onClick={() => it.has_image && setSelItem(it.id)}
                      className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 text-sm border-l-4 ${
                        on ? "border-tb-brand bg-tb-brand/10" : "border-transparent"
                      } ${it.has_image ? "hover:bg-tb-bg-2 dark:hover:bg-tb-d-inset" : "cursor-default"}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate">{it.label || "Item"}</span>
                        <span className="block text-xs text-tb-muted">
                          {it.merchant || "Manual entry"}
                          {it.receipt_date ? ` · ${date(it.receipt_date)}` : ""}
                          {it.has_image ? "" : " · no proof"}
                        </span>
                      </span>
                      <span className="tabular-nums shrink-0 font-medium">{money(it.amount)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* proof image */}
            <div className="p-5">
              <p className="text-sm font-semibold mb-3">Proof</p>
              {selItem ? (
                <ProofImage claimId={claim.id} itemId={selItem} />
              ) : (
                <div className="grid place-items-center h-48 rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset text-sm text-tb-muted">
                  No proof image on this claim
                </div>
              )}
            </div>

            {/* messages */}
            <div className="p-5 flex flex-col">
              <p className="text-sm font-semibold mb-3">Messages</p>
              <div className="flex-1 overflow-y-auto max-h-80 space-y-2 mb-3">
                {messages.length === 0 ? (
                  <p className="text-sm text-tb-muted">No messages yet.</p>
                ) : (
                  messages.map((m) => {
                    const mine = m.author_role === "company";
                    return (
                      <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${mine ? "ml-auto bg-tb-brand/10" : "bg-tb-bg-2 dark:bg-tb-d-inset"}`}>
                        <span className={`text-[11px] font-medium ${mine ? "text-tb-brand" : "text-tb-muted"}`}>
                          {mine ? "You" : (m.author_name || "Employee")} · {new Date(m.created_at).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <p className="mt-0.5">{m.body}</p>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="flex gap-2 mt-auto">
                <input value={msgInput} onChange={(e) => setMsgInput(e.target.value)}
                       placeholder="Write a message…"
                       onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMsg())}
                       className="flex-1 rounded-lg border border-tb-border dark:border-tb-d-border bg-white dark:bg-tb-d-inset px-3 py-2 text-sm outline-none focus:border-tb-brand" />
                <Button variant="subtle" onClick={sendMsg} disabled={sending || !msgInput.trim()}>Send</Button>
              </div>
            </div>
          </div>

          {/* actions */}
          {isOpen(claim.status) && (
            <div className="px-6 py-4 border-t border-tb-border dark:border-tb-d-border">
              {mode ? (
                <div>
                  <textarea
                    autoFocus
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={mode === "query"
                      ? "What does the sender need to fix or clarify?"
                      : "Reason for rejecting (optional)"}
                    className="w-full rounded-xl border border-tb-border dark:border-tb-d-border bg-white dark:bg-tb-d-inset px-3 py-2 text-sm outline-none focus:border-tb-brand focus:ring-1 focus:ring-tb-brand"
                  />
                  <div className="mt-3 flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => { setMode(null); setNote(""); }} disabled={busy}>
                      Cancel
                    </Button>
                    <Button
                      variant={mode === "reject" ? "danger" : "primary"}
                      onClick={mode === "query" ? onQuery : onReject}
                      disabled={busy}
                    >
                      {mode === "query" ? "Send query" : "Reject claim"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="ghost" onClick={() => setMode("reject")} disabled={busy}>Reject</Button>
                  <Button variant="subtle" onClick={() => setMode("query")} disabled={busy}>Query</Button>
                  <Button variant="primary" onClick={onPay} disabled={busy}>Mark paid</Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ---- proof image (authenticated blob fetch) -----------------------------
function ProofImage({ claimId, itemId }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let revoke;
    setSrc(null); setErr(false);
    (async () => {
      try {
        const res = await fetch(companyApi.claimItemImageUrl(claimId, itemId), {
          headers: { Authorization: `Bearer ${tokenStore.access}` },
        });
        if (!res.ok) { setErr(true); return; }
        const url = URL.createObjectURL(await res.blob());
        revoke = url;
        setSrc(url);
      } catch {
        setErr(true);
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [claimId, itemId]);

  if (err) {
    return (
      <div className="grid place-items-center h-48 rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset text-sm text-tb-muted">
        Image unavailable
      </div>
    );
  }
  if (!src) {
    return (
      <div className="grid place-items-center h-48 rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset">
        <Loading label="Loading proof" />
      </div>
    );
  }
  return (
    <a href={src} target="_blank" rel="noreferrer">
      <img src={src} alt="Proof" className="w-full rounded-xl border border-tb-border dark:border-tb-d-border" />
    </a>
  );
}